import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import '@theme/scrolling';

/**
 * @typedef {Object} CollectionBrowseRefs
 * @property {HTMLInputElement} [searchInput] - Search input element
 * @property {HTMLButtonElement} [resetButton] - Clear search button
 * @property {HTMLElement} [noResults] - No search results message
 * @property {HTMLElement} [toolbar] - Filters and search toolbar
 */

/**
 * @extends {Component<CollectionBrowseRefs>}
 */
class CollectionBrowse extends Component {
  /** @type {string} */
  #activeGroup = '';

  /** @type {boolean} */
  #scrollingProgrammatically = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #scrollRaf = null;

  connectedCallback() {
    super.connectedCallback();

    const firstBtn = this.querySelector('[data-filter-btn]');
    if (firstBtn instanceof HTMLElement) {
      this.#activeGroup = firstBtn.dataset.filterBtn ?? '';
    }

    this.#setupScrollSpy();
    this.#syncResetButton();
    this.#hideNoResults();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#cancelScrollAnimation();
  }

  /**
   * @param {MouseEvent} event
   */
  handleFilter(event) {
    const btn = /** @type {HTMLButtonElement} */ (event.target);
    const groupId = btn.dataset.filterBtn;
    if (!groupId) return;

    this.#setActiveButton(groupId);

    if (this.refs.searchInput?.value) {
      this.refs.searchInput.value = '';
      this.#showAllCards();
      this.#syncResetButton();
      this.#hideNoResults();
    }

    const group = this.querySelector(`[data-group="${groupId}"]`);
    if (!(group instanceof HTMLElement)) return;

    this.#scrollToGroup(group);
  }

  /**
   * @param {InputEvent} event
   */
  handleSearch(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const rawQuery = input.value.trim();
    const query = rawQuery.toLowerCase();

    this.#syncResetButton();

    if (query.length === 0) {
      this.#showAllCards();
      this.#hideNoResults();
      this.#syncActiveFromViewport();
      return;
    }

    this.#clearActiveButtons();

    let totalVisible = 0;

    for (const group of this.querySelectorAll('[data-group]')) {
      const groupEl = /** @type {HTMLElement} */ (group);
      let visibleCount = 0;

      for (const item of groupEl.querySelectorAll('.collection-browse__item')) {
        const title = item.querySelector('.collection-browse__card-title');
        const matches = title?.textContent?.toLowerCase().includes(query) ?? false;
        /** @type {HTMLElement} */ (item).hidden = !matches;
        if (matches) visibleCount++;
      }

      groupEl.hidden = visibleCount === 0;
      totalVisible += visibleCount;
    }

    if (totalVisible === 0) {
      this.#showNoResults(rawQuery);
    } else {
      this.#hideNoResults();
    }
  }

  clearSearch() {
    const { searchInput } = this.refs;
    if (!searchInput) return;

    searchInput.value = '';
    this.#showAllCards();
    this.#syncResetButton();
    this.#hideNoResults();
    this.#syncActiveFromViewport();
    searchInput.focus();
  }

  #syncResetButton() {
    const { searchInput, resetButton } = this.refs;
    if (!resetButton) return;

    resetButton.hidden = !searchInput?.value;
  }

  /**
   * @param {string} query
   */
  #showNoResults(query) {
    const { noResults } = this.refs;
    if (!noResults) return;

    const template = noResults.dataset.messageTemplate ?? '';
    noResults.textContent = template.replace('[query]', `"${query}"`);
    noResults.hidden = false;
  }

  #hideNoResults() {
    const { noResults } = this.refs;
    if (!noResults) return;

    noResults.hidden = true;
    noResults.textContent = '';
  }

  /**
   * Custom smooth scroll — Safari's native `behavior: 'smooth'` finishes too quickly.
   * @param {HTMLElement} group
   */
  #scrollToGroup(group) {
    const scrollMargin = Number.parseFloat(getComputedStyle(group).scrollMarginBlockStart) || 0;
    const targetTop = Math.max(0, window.scrollY + group.getBoundingClientRect().top - scrollMargin);

    this.#cancelScrollAnimation();
    this.#scrollingProgrammatically = true;

    if (prefersReducedMotion() || Math.abs(targetTop - window.scrollY) < 1) {
      window.scrollTo({ top: targetTop, behavior: 'instant' });
      this.#scrollingProgrammatically = false;
      return;
    }

    const startTop = window.scrollY;
    const distance = targetTop - startTop;
    // Keep a Chrome-like pace across browsers (Safari's native smooth is much faster).
    const duration = Math.min(900, Math.max(450, Math.abs(distance) * 0.55));
    /** @type {number | null} */
    let startTime = null;

    /**
     * @param {number} t
     */
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    /**
     * @param {DOMHighResTimeStamp} timestamp
     */
    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min(1, (timestamp - startTime) / duration);

      window.scrollTo({
        top: startTop + distance * easeInOutCubic(progress),
        behavior: 'instant',
      });

      if (progress < 1) {
        this.#scrollRaf = requestAnimationFrame(step);
        return;
      }

      this.#scrollRaf = null;
      this.#scrollingProgrammatically = false;
    };

    this.#scrollRaf = requestAnimationFrame(step);
  }

  #cancelScrollAnimation() {
    if (this.#scrollRaf !== null) {
      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }

    this.#scrollingProgrammatically = false;
  }

  #setupScrollSpy() {
    const groups = this.querySelectorAll('[data-group]');
    if (groups.length === 0) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (this.#scrollingProgrammatically) return;
        if (this.refs.searchInput?.value) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length === 0) return;

        const groupEl = /** @type {HTMLElement} */ (visible[0].target);
        const groupId = groupEl.dataset.group;
        if (groupId) this.#setActiveButton(groupId);
      },
      {
        root: null,
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75],
      }
    );

    for (const group of groups) {
      this.#observer.observe(group);
    }
  }

  /**
   * @param {string} groupId
   */
  #setActiveButton(groupId) {
    this.#activeGroup = groupId;

    for (const btn of this.querySelectorAll('[data-filter-btn]')) {
      const isActive = /** @type {HTMLElement} */ (btn).dataset.filterBtn === groupId;
      btn.classList.toggle('collection-browse__filter-btn--active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    }
  }

  #clearActiveButtons() {
    this.#activeGroup = '';

    for (const btn of this.querySelectorAll('[data-filter-btn]')) {
      btn.classList.remove('collection-browse__filter-btn--active');
      btn.setAttribute('aria-pressed', 'false');
    }
  }

  /** Re-select the tab for the group nearest the scroll-spy band after search ends. */
  #syncActiveFromViewport() {
    const groups = [];

    for (const group of this.querySelectorAll('[data-group]')) {
      if (group instanceof HTMLElement && !group.hidden) {
        groups.push(group);
      }
    }

    if (groups.length === 0) return;

    const probeY = window.innerHeight * 0.3;
    let best = groups[0];
    let bestDist = Infinity;

    for (const group of groups) {
      const rect = group.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) continue;

      const dist = Math.abs(rect.top - probeY);
      if (dist < bestDist) {
        bestDist = dist;
        best = group;
      }
    }

    const groupId = best?.dataset.group;
    if (groupId) this.#setActiveButton(groupId);
  }

  #showAllCards() {
    for (const group of this.querySelectorAll('[data-group]')) {
      const groupEl = /** @type {HTMLElement} */ (group);
      groupEl.hidden = false;

      for (const item of groupEl.querySelectorAll('.collection-browse__item')) {
        /** @type {HTMLElement} */ (item).hidden = false;
      }
    }
  }
}

if (!customElements.get('collection-browse')) {
  customElements.define('collection-browse', CollectionBrowse);
}
